import { useMemo, useState } from 'react';
import { DataGridPro, GridDensity, useGridApiRef } from '@mui/x-data-grid-pro';
import { useFetchBillingWeekQuery } from '@api/Trinium/Endpoints/Settlements/slice';
import { BillingWeekRecord } from '@api/Trinium/Endpoints/Settlements/types';
import { GenericOverlay } from '@components/DataGrid/GenericNoRowsOverlay';
import BrandLinearProgress from '@/Infrastructure/Components/Loading/BrandLinearProgress';
import { SettlementsToolbar } from '../SettlementsToolbar';
import { SummaryPanel, SummarySelection } from './SummaryPanel';
import { buildBillingWeekSummaries, columns } from './types';
import { settlementsGridSx, settlementsRowHeight } from '../gridStyles';
import { IStatementTab } from '../types';

import styles from './BillingWeekTab.module.css';

const EMPTY_SELECTION: SummarySelection = { service: null, day: null, customer: null };
// A stable fallback, so the memos below do not recompute on every render while the query is loading.
const EMPTY_RECORDS: BillingWeekRecord[] = [];

const gridSlots = {
  toolbar: SettlementsToolbar,
  loadingOverlay: BrandLinearProgress,
  noRowsOverlay: () => <GenericOverlay overlayText='No Billing Week Charges Found.' />
};

export default function BillingWeekTab(props: IStatementTab) {
  const { data: records = EMPTY_RECORDS, isFetching } = useFetchBillingWeekQuery({
    terminalNumber: props.terminalNumber,
    statementId: props.statementId
  });
  const apiRef = useGridApiRef();
  const [density, setDensity] = useState<GridDensity>('compact');
  const [selection, setSelection] = useState<SummarySelection>(EMPTY_SELECTION);

  /** Summaries always reflect the full statement, independent of any grid or summary filtering. */
  const summaries = useMemo(() => buildBillingWeekSummaries(records), [records]);

  const rows = useMemo(
    () =>
      records.filter(
        (row) =>
          (selection.service === null || row.serviceCode === selection.service) &&
          (selection.day === null || row.invoiceDate?.date === selection.day) &&
          (selection.customer === null || row.customerId === selection.customer)
      ),
    [records, selection]
  );

  return (
    <div className={styles.container}>
      <div className={styles['grid-area']}>
        <DataGridPro
          apiRef={apiRef}
          columns={columns}
          rows={rows}
          loading={isFetching}
          density={density}
          onDensityChange={setDensity}
          rowHeight={settlementsRowHeight}
          showToolbar
          disableRowSelectionOnClick
          slots={gridSlots}
          sx={settlementsGridSx}
        />
      </div>

      <SummaryPanel
        summaries={summaries}
        loading={isFetching}
        selection={selection}
        onSelectionChange={setSelection}
      />
    </div>
  );
}
